import { defineComponent as _defineComponent } from 'vue'
import { getPrettyCurrentDate } from '@@/bits/date'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { currentUrl, transformCurrentUrl } from '@@/bits/location'
import { PricingPlansExperimentVariant } from '@@/enums'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import OzScreenSizeAwareCenterModal from '@@/library/v4/components/OzScreenSizeAwareCenterModal.vue'
import {
  UpgradeModalCopyAExperimentVariant,
  UpgradeModalSingleStepExperimentVariant,
  useExperimentsStore,
} from '@@/pinia/experiments'
import { PaymentSchedule, Tier, UpgradeSource, UpgradeStep, usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { type User } from '@@/types'
import BillingCollectionItems from '@@/vuecomponents/BillingCollectionItems.vue'
import DashboardSettingsBillingModalTitle from '@@/vuecomponents/DashboardSettingsBillingModalTitle.vue'
import GoldMembershipTier, { Variant as GoldMembershipTierVariant } from '@@/vuecomponents/GoldMembershipTier.vue'
import MembershipTierActionButton from '@@/vuecomponents/MembershipTierActionButton.vue'
import NeonMembershipTier, { Variant as NeonMembershipTierVariant } from '@@/vuecomponents/NeonMembershipTier.vue'
import PaymentScheduleTier from '@@/vuecomponents/PaymentScheduleTier.vue'
import PlatinumMembershipTier, {
  Variant as PlatinumMembershipTierVariant,
} from '@@/vuecomponents/PlatinumMembershipTier.vue'
import SilverMembershipTier, { Variant as SilverMembershipTierVariant } from '@@/vuecomponents/SilverMembershipTier.vue'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'MembershipTierUpgradeModal',
  props: {
    user: null,
    whiteboardFramesUsed: { default: undefined },
    fileThatExceededSize: { default: undefined }
  },
  emits: ["cancel", "request-another-free-padlet"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'cancel'): void
  (e: 'request-another-free-padlet'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { user: User, whiteboardFramesUsed: number, fileThatExceededSize: File };





const currentDate = getPrettyCurrentDate()

const { isSmallerThanTabletPortrait, windowHeight } = storeToRefs(useWindowSizeStore())

const personalPlansStore = usePersonalPlansStore()
const {
  displayedEstimatedUpgradeCost,
  displayedPrices,
  displayedQuotas,
  isProcessingUpgrade,
  plans,
  plansFetched,
  advertisedQuotas,
  isLoadingAdvertisedQuotas,
  upgradeStep,
  upgradeError,
  upgradeSource,
  hasUserTriggeredUpgrade,
  estimatedCancellationRefundAmount,
  isLoadingCancellationRefundInformation,
} = storeToRefs(personalPlansStore)
const {
  upgradeToGold,
  upgradeToGoldDifferentCurrency,
  upgradeToPlatinum,
  upgradeToPlatinumDifferentCurrency,
  showPlanUpgradeToGoldModal,
  showUpgradeToPlatinumModalCheckingScheduledChanges,
  handleUpgrade,
} = personalPlansStore

const experimentsStore = useExperimentsStore()
const { upgradeModalCopyAExperimentVariant, upgradeModalSingleStepExperimentVariant } = storeToRefs(experimentsStore)

const xFrameLimitInsteadOfFileLimit = computed(() => upgradeStep.value === UpgradeStep.ChooseTierFrameQuota)

const currentPlanFrameLimit = computed(() => {
  switch (props.user.membership_tier) {
    case 'neon':
      return advertisedQuotas.value.neon.max_whiteboard_frames
    case 'silver':
      return advertisedQuotas.value.silver.max_whiteboard_frames
    case 'gold':
      return advertisedQuotas.value.gold.max_whiteboard_frames
    case 'platinum':
      return advertisedQuotas.value.platinum.max_whiteboard_frames
    default:
      return 0
  }
})

const padletQuotaNeonMembershipTierVariant = computed((): NeonMembershipTierVariant => {
  if (
    xFrameLimitInsteadOfFileLimit.value &&
    props.whiteboardFramesUsed &&
    advertisedQuotas.value.neon.max_whiteboard_frames
  ) {
    if (props.whiteboardFramesUsed > advertisedQuotas.value.neon.max_whiteboard_frames)
      return NeonMembershipTierVariant.OverFrameQuota
    if (props.whiteboardFramesUsed === advertisedQuotas.value.neon.max_whiteboard_frames)
      return NeonMembershipTierVariant.AtFrameQuota
    return NeonMembershipTierVariant.UnderFrameQuota
  }

  if (props.user.quota.walls_used > advertisedQuotas.value.neon.max_walls!)
    return NeonMembershipTierVariant.OverPadletQuota
  if (props.user.quota.walls_used === advertisedQuotas.value.neon.max_walls)
    return NeonMembershipTierVariant.AtPadletQuota
  return NeonMembershipTierVariant.UnderPadletQuota
})

const padletQuotaSilverMembershipTierVariant = computed((): SilverMembershipTierVariant => {
  if (
    xFrameLimitInsteadOfFileLimit.value &&
    props.whiteboardFramesUsed &&
    advertisedQuotas.value.silver.max_whiteboard_frames
  ) {
    if (props.user.membership_tier === 'silver') {
      if (props.whiteboardFramesUsed > advertisedQuotas.value.silver.max_whiteboard_frames)
        return SilverMembershipTierVariant.OverFrameQuota
      if (props.whiteboardFramesUsed === advertisedQuotas.value.silver.max_whiteboard_frames)
        return SilverMembershipTierVariant.AtFrameQuota
      return SilverMembershipTierVariant.UnderFrameQuota
    }

    if (props.whiteboardFramesUsed > advertisedQuotas.value.silver.max_whiteboard_frames) {
      return SilverMembershipTierVariant.OverFrameQuota
    }
    return SilverMembershipTierVariant.Upgrade
  }

  if (props.user.membership_tier === 'silver') {
    if (props.user.quota.walls_used > advertisedQuotas.value.silver.max_walls!)
      return SilverMembershipTierVariant.OverPadletQuota
    if (props.user.quota.walls_used === advertisedQuotas.value.silver.max_walls)
      return SilverMembershipTierVariant.AtPadletQuota
    return SilverMembershipTierVariant.UnderPadletQuota
  }
  if (props.user.quota.walls_used > 10) return SilverMembershipTierVariant.OverPadletQuota
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return SilverMembershipTierVariant.UpgradeSingleStep
  return SilverMembershipTierVariant.Upgrade
})

const padletQuotaGoldMembershipTierVariant = computed((): GoldMembershipTierVariant => {
  if (
    xFrameLimitInsteadOfFileLimit.value &&
    props.whiteboardFramesUsed &&
    advertisedQuotas.value.gold.max_whiteboard_frames
  ) {
    if (props.user.membership_tier === 'gold') {
      if (props.whiteboardFramesUsed > advertisedQuotas.value.gold.max_whiteboard_frames)
        return GoldMembershipTierVariant.OverFrameQuota
      if (props.whiteboardFramesUsed === advertisedQuotas.value.gold.max_whiteboard_frames)
        return GoldMembershipTierVariant.AtFrameQuota
      return GoldMembershipTierVariant.UnderFrameQuota
    }

    if (props.whiteboardFramesUsed > advertisedQuotas.value.gold.max_whiteboard_frames) {
      return GoldMembershipTierVariant.OverFrameQuota
    }
    return GoldMembershipTierVariant.Upgrade
  }

  if (props.user.membership_tier === 'gold') {
    if (props.user.quota.walls_used > advertisedQuotas.value.gold.max_walls!)
      return GoldMembershipTierVariant.OverPadletQuota
    if (props.user.quota.walls_used === advertisedQuotas.value.gold.max_walls)
      return GoldMembershipTierVariant.AtPadletQuota
    return GoldMembershipTierVariant.UnderPadletQuota
  }
  if (props.user.quota.walls_used > 20) return GoldMembershipTierVariant.OverPadletQuota
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return GoldMembershipTierVariant.UpgradeSingleStep
  return GoldMembershipTierVariant.Upgrade
})

const platinumMembershipTierVariant = computed((): PlatinumMembershipTierVariant => {
  // We don't need to handle whiteboard because we don't allow the modal to be shown for whiteboards at/over the platinum limit

  if (hasUserTriggeredUpgrade.value) return PlatinumMembershipTierVariant.Loading
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation &&
    props.user.membership_tier === 'neon'
  )
    return PlatinumMembershipTierVariant.UpgradeSingleStep
  return PlatinumMembershipTierVariant.Upgrade
})

const fileSizeQuotaSilverMembershipTierVariant = computed((): SilverMembershipTierVariant => {
  if (
    props.user.membership_tier === 'silver' ||
    sizeOfFileOverQuota.value > advertisedQuotas.value.silver.max_upload_megabytes! * 1000000
  )
    return SilverMembershipTierVariant.FileSize
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return GoldMembershipTierVariant.UpgradeSingleStep
  return SilverMembershipTierVariant.Upgrade
})

const fileSizeQuotaGoldMembershipTierVariant = computed((): GoldMembershipTierVariant => {
  if (
    props.user.membership_tier === 'gold' ||
    sizeOfFileOverQuota.value > advertisedQuotas.value.gold.max_upload_megabytes! * 1000000
  )
    return GoldMembershipTierVariant.FileSize
  if (
    xPlatinumMonthlyPrice.value &&
    upgradeModalSingleStepExperimentVariant.value === UpgradeModalSingleStepExperimentVariant.Variation
  )
    return GoldMembershipTierVariant.UpgradeSingleStep
  return GoldMembershipTierVariant.Upgrade
})

const sizeOfFileOverQuota = computed(() => {
  return props.fileThatExceededSize ? props.fileThatExceededSize.size : 0
})

const membershipConfirmationPageUrl = computed((): string => {
  return transformCurrentUrl({}, { path: '/memberships/confirmation', search: { next: currentUrl() } })
})

const chooseTierPadletQuotaTitle = computed((): string => {
  if (
    upgradeSource.value === UpgradeSource.UntrashWallQuota ||
    upgradeSource.value === UpgradeSource.UnarchiveWallQuota
  ) {
    return __("You've reached your padlet quota limit.")
  }

  return __('We see you flying. Maybe it’s time to soar?')
})

const chooseTierPadletQuotaSubtitle = computed((): string => {
  if (upgradeSource.value === UpgradeSource.UntrashWallQuota) {
    return __('Upgrade to restore this padlet.')
  }
  if (upgradeSource.value === UpgradeSource.UnarchiveWallQuota) {
    return __('Upgrade to unarchive this padlet.')
  }
  return __('Upgrade your membership to continue making padlets.')
})

const xGoldMonthlyPrice = computed((): boolean => {
  if (!isAppUsing('personalPricingPlansExperiment')) {
    return true
  }
  // Show gold monthly plan when variant is 'platinum_only' or 'control'
  if (props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.AnnualOnly) {
    return false
  }
  return true
})

const xPlatinumMonthlyPrice = computed((): boolean => {
  // Show platinum monthly plan when variant is 'platinum_only' or 'control'
  if (
    (isAppUsing('personalPricingPlansExperiment') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.AnnualOnly) ||
    (isAppUsing('personalPricingPlansExperimentV2') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumSilverAnnualOnly)
  ) {
    return false
  }
  return true
})

const xGoldPlans = computed((): boolean => {
  // Show gold plans when variant is 'annual_only' or 'control'
  if (
    (isAppUsing('personalPricingPlansExperiment') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumOnly) ||
    (isAppUsing('personalPricingPlansExperimentV2') &&
      props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumSilverAnnualOnly)
  ) {
    return false
  }
  return true
})

const xSilverPlans = computed((): boolean => {
  // Show silver plans when variant is 'platinum_silver_annual_only'
  if (!isAppUsing('personalPricingPlansExperimentV2')) {
    return false
  }
  return props.user.personal_pricing_plan_variant === PricingPlansExperimentVariant.PlatinumSilverAnnualOnly
})

function initiateUpgradeToGold() {
  if (props.user.membership_tier == null) return

  if (!xGoldMonthlyPrice.value) {
    handleUpgrade({
      paymentSchedule: PaymentSchedule.Annual,
      tier: Tier.Gold,
      postCheckoutRedirectUrl: membershipConfirmationPageUrl.value,
    })
    return
  }

  showPlanUpgradeToGoldModal({ upgradeSource: null })
}

function initiateUpgradeToSilver() {
  handleUpgrade({
    paymentSchedule: PaymentSchedule.Annual,
    tier: Tier.Silver,
    postCheckoutRedirectUrl: membershipConfirmationPageUrl.value,
  })
}

function initiateUpgradeToPlatinum() {
  if (props.user.membership_tier == null) return
  if (!xPlatinumMonthlyPrice.value && props.user.membership_tier === 'neon') {
    handleUpgrade({
      paymentSchedule: PaymentSchedule.Annual,
      tier: Tier.Platinum,
      postCheckoutRedirectUrl: membershipConfirmationPageUrl.value,
    })
    return
  }

  showUpgradeToPlatinumModalCheckingScheduledChanges({
    membershipTier: props.user.membership_tier,
    paymentSchedule: props.user.period ?? undefined,
  })
}

// Every method that confirms an upgrade has the same call signature
// This function handles input validation before calling such a method.
function confirmUpgrade(upgradeFn: (payload: { paymentSchedule: string }) => void) {
  if (props.user.period == null) return

  upgradeFn({ paymentSchedule: props.user.period })
}

const hasRequestedAnotherFreePadlet = ref(false)

const xGetAnotherFreePadletButton = computed((): boolean => {
  if (props.user.tenant_id !== 1) return false
  if (!props.user.quota?.can_make) return false
  if (hasRequestedAnotherFreePadlet.value) return false
  if (
    props.user.membership_tier === 'neon' &&
    (props.user.quota.walls_used === 3 || props.user.quota.walls_used === 4)
  ) {
    return true
  }
  if (
    props.user.membership_tier === 'gold' &&
    (props.user.quota.walls_used === 20 || props.user.quota.walls_used === 21)
  ) {
    return true
  }
  return false
})

function getAnotherFreePadlet(): void {
  hasRequestedAnotherFreePadlet.value = true
  emit('request-another-free-padlet')
}

function getTierSelectionTitleFromExperiment(defaultTitle: string): string {
  if (xFrameLimitInsteadOfFileLimit.value) {
    return __("You've hit your limit of %{currentPlanFrameLimit} cards.", {
      currentPlanFrameLimit: currentPlanFrameLimit.value,
    })
  }

  return upgradeModalCopyAExperimentVariant.value === UpgradeModalCopyAExperimentVariant.Control
    ? defaultTitle
    : __('Upgrade and join 3.5 million paying users.')
}

function getTierSelectionSubtitleFromExperiment(defaultTitle: string): string {
  if (xFrameLimitInsteadOfFileLimit.value) {
    return __('Upgrade your membership to add more.')
  }

  return upgradeModalCopyAExperimentVariant.value === UpgradeModalCopyAExperimentVariant.Control
    ? defaultTitle
    : __('Cancel anytime with our flexible refund policy.')
}

return { __sfc: true,props, emit, currentDate, isSmallerThanTabletPortrait, windowHeight, personalPlansStore, displayedEstimatedUpgradeCost, displayedPrices, displayedQuotas, isProcessingUpgrade, plans, plansFetched, advertisedQuotas, isLoadingAdvertisedQuotas, upgradeStep, upgradeError, upgradeSource, hasUserTriggeredUpgrade, estimatedCancellationRefundAmount, isLoadingCancellationRefundInformation, upgradeToGold, upgradeToGoldDifferentCurrency, upgradeToPlatinum, upgradeToPlatinumDifferentCurrency, showPlanUpgradeToGoldModal, showUpgradeToPlatinumModalCheckingScheduledChanges, handleUpgrade, experimentsStore, upgradeModalCopyAExperimentVariant, upgradeModalSingleStepExperimentVariant, xFrameLimitInsteadOfFileLimit, currentPlanFrameLimit, padletQuotaNeonMembershipTierVariant, padletQuotaSilverMembershipTierVariant, padletQuotaGoldMembershipTierVariant, platinumMembershipTierVariant, fileSizeQuotaSilverMembershipTierVariant, fileSizeQuotaGoldMembershipTierVariant, sizeOfFileOverQuota, membershipConfirmationPageUrl, chooseTierPadletQuotaTitle, chooseTierPadletQuotaSubtitle, xGoldMonthlyPrice, xPlatinumMonthlyPrice, xGoldPlans, xSilverPlans, initiateUpgradeToGold, initiateUpgradeToSilver, initiateUpgradeToPlatinum, confirmUpgrade, hasRequestedAnotherFreePadlet, xGetAnotherFreePadletButton, getAnotherFreePadlet, getTierSelectionTitleFromExperiment, getTierSelectionSubtitleFromExperiment, __, OzBox, OzBoxColors, OzCloseButton, OzContainedButton, OzContainedButtonColorScheme, OzContainedButtonSizePreset, OzDivider, OzDividerColorScheme, OzIndeterminateSpinner, OzScreenSizeAwareCenterModal, PaymentSchedule, Tier, UpgradeStep, BillingCollectionItems, DashboardSettingsBillingModalTitle, GoldMembershipTier, MembershipTierActionButton, NeonMembershipTier, NeonMembershipTierVariant, PaymentScheduleTier, PlatinumMembershipTier, SilverMembershipTier }
}

})